import { z } from 'zod'

import { inventoryAllocationFields } from '../types/inventory-allocation'
import { isCalendarDate } from '../utils/calendar-date'

const integer = z.number().int().min(-2147483647).max(2147483647)
const day = z.string().refine(isCalendarDate, 'Choose a valid calendar date')
const common = {
  itemId: z.string().uuid(),
  reason: z.string().trim().min(1).max(100),
  notes: z.string().optional(),
}
export const stockMovementPayloadSchema = z
  .object({
    ...common,
    ...inventoryAllocationFields,
    type: z.enum(['IN', 'OUT', 'TRANSFER', 'ADJUSTMENT']),
    quantity: integer,
    unitCost: z.number().finite().min(0).max(99999999.99).optional(),
    fromLocationId: z.string().uuid().optional(),
    toLocationId: z.string().uuid().optional(),
    movementDate: day,
  })
  .strict()
  .refine(
    value =>
      value.type === 'ADJUSTMENT' ? value.quantity !== 0 : value.quantity > 0,
    'Enter a whole quantity; only adjustments can be negative',
  )
export const stockCountPayloadSchema = z
  .object({
    ...common,
    reason: z.enum(['CYCLE_COUNT', 'DAMAGE', 'THEFT', 'CORRECTION']),
    physicalCount: integer.min(0),
    // Keep older recovery receipts readable; the server only permits their replay.
    expectedQuantity: integer.min(0).optional(),
    locationId: z.string().uuid().optional(),
    adjustmentDate: day,
  })
  .strict()
export type StockMovementPayload = z.infer<typeof stockMovementPayloadSchema>
export type StockCountPayload = z.infer<typeof stockCountPayloadSchema>
export type StockCommand<T> = T & { operationId: string }
/** Never truncate a fraction or accept a numeric prefix from malformed text. */
export function parseStockInteger(input: string | number): number | undefined {
  if (!/^-?\d+$/.test(String(input).trim())) return undefined
  const parsed = integer.safeParse(Number(input))
  return parsed.success ? parsed.data : undefined
}
